import { useProtocolIncomeQuery } from "~/types/graphql";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import ProtocolIncomeChart from "~/components/ProtocolIncomeChart";
import { ExpandableChart } from "~/components/ExpandableChart";

const formatUSD = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function ProtocolIncomePage() {
  const { data, isLoading, error } = useProtocolIncomeQuery();

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading protocol income data</div>;
  if (!data) return null;

  // Combine the data from different collections by timestamp
  const combinedData = new Map();
  let cumulativeDefaultIncome = 0;
  let cumulativeInterestIncome = 0;

  // Initialize with default stats
  data.defaultStats_collection.forEach((item) => {
    if (!item.timestamp) return;
    const defaultIncome =
      parseFloat(item.totalValueClaimed || "0") -
      parseFloat(item.totalPrincipalDefaulted || "0");
    cumulativeDefaultIncome += defaultIncome;
    combinedData.set(item.timestamp, {
      timestamp: item.timestamp,
      totalValueClaimed: item.totalValueClaimed as string,
      totalPrincipalDefaulted: item.totalPrincipalDefaulted as string,
      totalNewInterest: "0",
      totalInterestPaid: "0",
      cumulativeDefaultIncome: cumulativeDefaultIncome.toString(),
      cumulativeInterestIncome: "0",
    });
  });

  // Add extension stats
  data.extensionStats_collection.forEach((item) => {
    if (!item.timestamp) return;
    const interestIncome = parseFloat(item.totalNewInterest || "0");
    cumulativeInterestIncome += interestIncome;
    const existing = combinedData.get(item.timestamp) || {
      timestamp: item.timestamp,
      totalValueClaimed: "0",
      totalPrincipalDefaulted: "0",
      totalInterestPaid: "0",
      cumulativeDefaultIncome: cumulativeDefaultIncome.toString(),
      cumulativeInterestIncome: cumulativeInterestIncome.toString(),
    };
    combinedData.set(item.timestamp, {
      ...existing,
      totalNewInterest: item.totalNewInterest as string,
    });
  });

  // Add repayment stats
  data.repaymentStats_collection.forEach((item) => {
    if (!item.timestamp) return;
    const interestIncome = parseFloat(item.totalInterestPaid || "0");
    cumulativeInterestIncome += interestIncome;
    const existing = combinedData.get(item.timestamp) || {
      timestamp: item.timestamp,
      totalValueClaimed: "0",
      totalPrincipalDefaulted: "0",
      totalNewInterest: "0",
      cumulativeDefaultIncome: cumulativeDefaultIncome.toString(),
      cumulativeInterestIncome: cumulativeInterestIncome.toString(),
    };
    combinedData.set(item.timestamp, {
      ...existing,
      totalInterestPaid: item.totalInterestPaid as string,
    });
  });

  // Convert map to array and sort by timestamp
  const chartData = Array.from(combinedData.values())
    .filter((item) => item.timestamp) // Remove any items with invalid timestamps
    .sort((a, b) => Number(a.timestamp) - Number(b.timestamp));

  return (
    <div className="grid grid-cols-2 gap-4 pt-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            Total Default Income
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Total income from defaults (claimed value minus defaulted
                    principal)
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatUSD(cumulativeDefaultIncome)}
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            Total Interest Income
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Total income from interest payments</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {formatUSD(cumulativeInterestIncome)}
          </div>
        </CardContent>
      </Card>
      <div className="col-span-2">
        <ExpandableChart>
          <ProtocolIncomeChart data={chartData} />
        </ExpandableChart>
      </div>
    </div>
  );
}
